<template>
  <div class="eod-modern-dashboard">
    <div class="card shadow-sm mb-4">
      <div class="card-body eod-toolbar-card">
        <div class="row align-items-end">
          <div class="col-xl-2 col-lg-3 col-md-6 mb-3">
            <label class="eod-toolbar-label">Business Date</label>
            <a-date-picker
              :value="localBusinessDate"
              :format="dateFormat"
              class="w-100"
              @change="onBusinessDateChange"
            />
          </div>

          <div class="col-xl-2 col-lg-3 col-md-6 mb-3">
            <label class="eod-toolbar-label">Site</label>
            <a-select :value="localFilters.siteId" class="w-100" :disabled="isBranch" @change="onSiteChange">
              <a-select-option key="0" value="0">All Sites</a-select-option>
              <a-select-option v-for="site in sites" :key="site.id" :value="String(site.id)">
                {{ site.name }}
              </a-select-option>
            </a-select>
          </div>

          <div class="col-xl-3 col-lg-6 col-md-12 mb-3">
            <label class="eod-toolbar-label">Terminal</label>
            <a-select
              :value="localFilters.terminalId"
              class="w-100"
              mode="multiple"
              show-search
              option-filter-prop="children"
              placeholder="All terminals"
              @change="onTerminalChange"
            >
              <a-select-option v-for="terminal in terminals" :key="terminal.id" :value="String(terminal.id)">
                {{ terminal.name }}
              </a-select-option>
            </a-select>
          </div>

          <div class="col-xl-2 col-lg-3 col-md-6 mb-3">
            <label class="eod-toolbar-label">Status</label>
            <a-select :value="localFilters.status" class="w-100" @change="value => localFilters.status = value">
              <a-select-option value="O">All</a-select-option>
              <a-select-option value="C">Completed</a-select-option>
              <a-select-option value="P">Pending Z-read</a-select-option>
              <a-select-option value="N">No activity</a-select-option>
              <a-select-option value="E">Exception</a-select-option>
            </a-select>
          </div>

          <div class="col-xl-2 col-lg-3 col-md-6 mb-3">
            <label class="eod-toolbar-label">Issue Type</label>
            <a-select :value="localFilters.issueFilter" class="w-100" @change="value => localFilters.issueFilter = value">
              <a-select-option value="">All</a-select-option>
              <a-select-option value="missing_z">Missing Z-read</a-select-option>
              <a-select-option value="count">Count mismatch</a-select-option>
              <a-select-option value="gross">Gross mismatch</a-select-option>
              <a-select-option value="net">Net mismatch</a-select-option>
              <a-select-option value="multiple">Multiple issues</a-select-option>
            </a-select>
          </div>

          <div class="col-xl-1 col-lg-3 col-md-6 mb-3">
            <label class="eod-toolbar-label">Exceptions</label>
            <div class="eod-toolbar-switch">
              <a-switch :checked="localFilters.mismatchOnly" @change="value => localFilters.mismatchOnly = value" />
            </div>
          </div>

          <div class="col-xl-3 col-lg-4 col-md-6 mb-3 eod-toolbar-actions">
            <a-button type="primary" @click="applyFilters">Apply</a-button>
            <a-button class="ml-2" @click="resetFilters">Reset</a-button>
            <a-button class="ml-2" @click="refreshData">
              <a-icon type="reload" />
            </a-button>
          </div>
        </div>
      </div>
    </div>

    <div class="card shadow-sm mb-4 eod-health-card">
      <div class="card-body">
        <div class="row align-items-center">
          <div class="col-lg-8 mb-3 mb-lg-0">
            <p class="eod-overline mb-2">Branch Health</p>
            <h3 class="mb-2">{{ healthHeadline }}</h3>
            <p class="text-muted mb-0">{{ healthStatusLine }}</p>
          </div>
          <div class="col-lg-4">
            <div class="row text-lg-right">
              <div class="col-6 mb-2">
                <div class="eod-health-metric">{{ summaryValue('total_terminal_count') }}</div>
                <small class="text-muted">Active terminals</small>
              </div>
              <div class="col-6 mb-2">
                <div class="eod-health-metric">{{ summaryValue('with_zread_count') }}</div>
                <small class="text-muted">With Z-read</small>
              </div>
              <div class="col-6">
                <div class="eod-health-metric">{{ summaryValue('pos_only_count') }}</div>
                <small class="text-muted">POS docs only</small>
              </div>
              <div class="col-6">
                <div class="eod-health-metric">{{ summaryValue('no_activity_count') }}</div>
                <small class="text-muted">No activity</small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="card shadow-sm">
      <div class="card-header border-0 d-flex justify-content-between align-items-center flex-wrap">
        <div>
          <h3 class="mb-0">Terminal Reconciliation</h3>
          <small class="text-muted">Exceptions first, with reconciliation basis surfaced inline.</small>
        </div>
        <div class="text-muted small mt-2 mt-md-0">
          <!-- POS version is shown per terminal when available. -->
        </div>
      </div>

      <div class="table-responsive">
        <table class="table table-sm table-hover align-items-center mb-0 eod-modern-table">
          <thead class="thead-light">
            <tr>
              <th>Terminal</th>
              <th>Status</th>
              <th>Issue Type</th>
              <th>Documents</th>
              <th>Gross Sales</th>
              <th>Net Sales</th>
              <th class="text-center">Actions</th>
            </tr>
          </thead>
          <tbody v-if="loadingRows">
            <tr>
              <td colspan="7" class="text-center py-5">
                <a-spin />
              </td>
            </tr>
          </tbody>
          <tbody v-else-if="filteredRows.length === 0">
            <tr>
              <td colspan="7" class="text-center text-muted py-5">
                No terminals matched the current filters.
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr v-for="row in filteredRows" :key="rowKey(row)" :class="rowClass(row)">
              <td>
                <div class="font-weight-600">{{ row.terminal_name }}</div>
                <div class="small text-muted">{{ row.machine_no || row.terminal_code }}</div>
                <div class="small text-muted">{{ row.site_name }}</div>
              </td>
              <td>
                <span class="badge badge-pill" :class="statusBadgeClass(row)">
                  {{ statusLabel(row) }}
                </span>
              </td>
              <td>
                <div class="font-weight-600">{{ row.issue_type }}</div>
                <small class="text-muted">POS Version: {{ row.z_pos_version || '5.0' }}</small>
              </td>
              <td>
                <div class="eod-metric-pair">
                  <span>Z-Reading</span>
                  <strong>{{ integerValue(row.z_expected_document_count) }}</strong>
                </div>
                <div class="eod-metric-pair">
                  <span>POS</span>
                  <strong>{{ integerValue(row.pos_document_count) }}</strong>
                </div>
                <div class="small" :class="diffTextClass(row.document_count_match)">
                  Diff {{ signedInteger(row.document_count_diff) }}
                </div>
              </td>
              <td>
                <div class="eod-metric-pair">
                  <span>Z-Reading</span>
                  <strong>{{ formatAmount(row.z_gross_sales) }}</strong>
                </div>
                <div class="eod-metric-pair">
                  <span>POS</span>
                  <strong>{{ formatAmount(row.pos_gross_sales) }}</strong>
                </div>
                <div class="small" :class="diffTextClass(row.gross_sales_match)">
                  Diff {{ formatSignedAmount(row.gross_sales_diff) }}
                </div>
              </td>
              <td>
                <div class="eod-metric-pair">
                  <span>Z-Reading</span>
                  <strong>{{ formatAmount(row.z_net_sales) }}</strong>
                </div>
                <div class="eod-metric-pair">
                  <span>POS</span>
                  <strong>{{ formatAmount(row.pos_net_sales) }}</strong>
                </div>
                <div class="small" :class="diffTextClass(row.net_sales_match)">
                  Diff {{ formatSignedAmount(row.net_sales_diff) }}
                </div>
              </td>
              <td class="text-center">
                <a-button size="small" @click="openDrawer(row)" :disabled="Number(row.missing_z_reading) === 1">
                  Show Details
                </a-button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="card-footer d-flex justify-content-between align-items-center flex-wrap">
        <small class="text-muted mb-2 mb-md-0">
          Showing {{ paginationStart }} - {{ paginationEnd }} of {{ totalRows }} terminals.
        </small>
        <a-pagination
          :current="page"
          :page-size="perPage"
          :total="totalRows"
          :show-size-changer="false"
          @change="onPageChange"
        />
      </div>
    </div>

    <a-drawer :width="520" placement="right" :visible="drawerVisible" @close="closeDrawer">
      <template slot="title">
        <div>
          <strong>{{ selectedRow ? selectedRow.terminal_name : 'Terminal details' }}</strong>
          <div class="small text-muted">{{ selectedRow ? selectedRow.business_date : '' }}</div>
        </div>
      </template>

      <template v-if="selectedRow">
        <div class="eod-drawer-section">
          <p class="eod-overline mb-2">Reconciliation Summary</p>
          <div class="eod-summary-banner" :class="summaryBannerClass(selectedRow)">
            <div>
              <div class="d-flex align-items-center gap-2 flex-wrap">
                <span class="badge badge-pill" :class="statusBadgeClass(selectedRow)">
                  {{ statusLabel(selectedRow) }}
                </span>
                <strong class="eod-summary-title">{{ summaryHeadline(selectedRow) }}</strong>
              </div>
              <p class="mb-0 mt-2">{{ summaryHint(selectedRow) }}</p>
            </div>
          </div>

          <div class="eod-summary-grid mt-3">
            <div class="eod-summary-card" :class="summaryCardClass(selectedRow.document_count_match)">
              <span class="eod-summary-card-label">Documents</span>
              <strong>{{ matchText(selectedRow.document_count_match) }}</strong>
              <small>Z-read {{ integerValue(selectedRow.z_expected_document_count) }} | POS {{ integerValue(selectedRow.pos_document_count) }}</small>
              <small>Diff {{ signedInteger(selectedRow.document_count_diff) }}</small>
            </div>
            <div class="eod-summary-card" :class="summaryCardClass(selectedRow.gross_sales_match)">
              <span class="eod-summary-card-label">Gross Sales</span>
              <strong>{{ matchText(selectedRow.gross_sales_match) }}</strong>
              <small>Z-read {{ formatAmount(selectedRow.z_gross_sales) }} | POS {{ formatAmount(selectedRow.pos_gross_sales) }}</small>
              <small>Diff {{ formatSignedAmount(selectedRow.gross_sales_diff) }}</small>
            </div>
            <div class="eod-summary-card" :class="summaryCardClass(selectedRow.net_sales_match)">
              <span class="eod-summary-card-label">Net Sales</span>
              <strong>{{ matchText(selectedRow.net_sales_match) }}</strong>
              <small>Z-read {{ formatAmount(selectedRow.z_net_sales) }} | POS {{ formatAmount(selectedRow.pos_net_sales) }}</small>
              <small>Diff {{ formatSignedAmount(selectedRow.net_sales_diff) }}</small>
            </div>
          </div>

          <div class="eod-detail-grid mt-3">
            <div>
              <span>Issue Type</span>
              <strong>{{ selectedRow.issue_type }}</strong>
            </div>
            <div>
              <span>POS Version</span>
              <strong>{{ selectedRow.z_pos_version || '5.0' }}</strong>
            </div>
            <div>
              <span>POS Range</span>
              <strong>{{ integerValue(selectedRow.pos_min_trans_id) }} - {{ integerValue(selectedRow.pos_max_trans_id) }}</strong>
            </div>
            <div>
              <span>Missing POS</span>
              <strong>{{ yesNo(selectedRow.missing_pos_documents) }}</strong>
            </div>
          </div>
        </div>

        <div class="eod-drawer-section">
          <h6 class="heading-small text-muted mb-4">Z-READING PREVIEW</h6>
          <div v-if="loadingPreview" class="text-center py-4">
            <a-spin />
          </div>
          <div v-else-if="Number(selectedRow.missing_z_reading) === 1" class="eod-empty-preview">
            No Z-read exists for this terminal and business date.
          </div>
          <div v-else-if="zreadData" class="eod-zread-preview">
            <div v-if="zreadData.pos_version == '5.1'" class="eod-zread-preview-body">
              <table width="100%">
                <tr><td align="left">Terminal:</td><td></td><td></td><td align="right">{{ zreadData.terminal }}</td></tr>
                <tr><td align="left">Printed by:</td><td></td><td></td><td align="right">{{ zreadData.printedBy }}</td></tr>
                <tr><td colspan="4">&nbsp;</td></tr>
                <tr><td align="left">Start Date & Time:</td><td></td><td></td><td align="right">{{ zreadData.dateFrom }}</td></tr>
                <tr><td align="left">End Date & Time:</td><td></td><td></td><td align="right">{{ zreadData.dateTo }}</td></tr>
                <tr><td align="left">Business Date:</td><td></td><td></td><td align="right">{{ zreadData.businessDate }}</td></tr>
                <tr><td colspan="4">&nbsp;</td></tr>
                <tr><td align="left">BEGINNING SI</td><td>#:</td><td></td><td align="right">{{ formatPadded(zreadData.beginningSi, 9) }}</td></tr>
                <tr><td align="left">ENDING SI</td><td>#:</td><td></td><td align="right">{{ formatPadded(zreadData.endingSi, 9) }}</td></tr>
                <tr><td align="left">BEGINNING Trans</td><td>#:</td><td></td><td align="right">{{ formatPadded(zreadData.firstTransactionId, 9) }}</td></tr>
                <tr><td align="left">ENDING Trans</td><td>#:</td><td></td><td align="right">{{ formatPadded(zreadData.lastTransactionId, 9) }}</td></tr>
                <tr><td align="left">Transaction Count</td><td>:</td><td></td><td align="right">{{ formatPadded(zreadData.totalTransactionCount, 9) }}</td></tr>
              </table>
              <br>
              <table width="100%">
                <tr><td align="left">Reset Counter No.</td><td></td><td align="right">{{ zreadData.resetCounterSI || 0 }}</td></tr>
                <tr><td align="left">Z Counter No. :</td><td></td><td align="right">{{ formatPadded(zreadData.zReadingCounter, 5) }}</td></tr>
                <tr><td colspan="3">-----------------------------------------------------------------------------------------</td></tr>
                <tr><td align="left">Present Accum Sales:</td><td></td><td align="right">{{ formatClassicAmount(zreadData.endingGrossBalance) }}</td></tr>
                <tr><td align="left">Previous Accum Sales:</td><td></td><td align="right">{{ formatClassicAmount(zreadData.beginningGrossBalance) }}</td></tr>
                <tr><td align="left">Sales for the Day:</td><td></td><td align="right">{{ formatClassicAmount(zreadData.grossSales && zreadData.grossSales.total) }}</td></tr>
                <tr><td colspan="3">-----------------------------------------------------------------------------------------</td></tr>
                <tr><th colspan="3" style="text-align: center">BREAK DOWN OF SALES</th></tr>
                <tr><td align="left">VATABLE:</td><td></td><td align="right">{{ formatClassicAmount(zreadData.grossSales && zreadData.grossSales.vatable) }}</td></tr>
                <tr><td align="left">VAT AMOUNT:</td><td></td><td align="right">{{ formatClassicAmount(zreadData.grossSales && zreadData.grossSales.vatAmount) }}</td></tr>
                <tr><td align="left">VAT-EXEMPT:</td><td></td><td align="right">{{ formatClassicAmount(zreadData.grossSales && zreadData.grossSales.vatExempt) }}</td></tr>
                <tr><td align="left">ZERO-RATED:</td><td></td><td align="right">{{ formatClassicAmount(zreadData.grossSales && zreadData.grossSales.zeroRated) }}</td></tr>
                <tr><td colspan="3">-----------------------------------------------------------------------------------------</td></tr>
                <tr><td align="left">Gross Amount:</td><td></td><td align="right">{{ formatClassicAmount(zreadData.grossSales && zreadData.grossSales.total) }}</td></tr>
                <tr><td align="left">Less Discount:</td><td></td><td align="right">{{ formatClassicAmount(totalDiscountValue(zreadData)) }}</td></tr>
                <tr><td align="left">Less Return:</td><td></td><td align="right">{{ formatClassicAmount(zreadData.deductions && zreadData.deductions.returns) }}</td></tr>
                <tr><td align="left">Less Refund:</td><td></td><td align="right">{{ formatClassicAmount(zreadData.deductions && zreadData.deductions.refund) }}</td></tr>
                <tr><td align="left">Less Void:</td><td></td><td align="right">{{ formatClassicAmount(zreadData.deductions && zreadData.deductions.void) }}</td></tr>
                <tr><td align="left">Less VAT Adjustment:</td><td></td><td align="right">{{ formatClassicAmount(zreadData.adjustmentsOnVat && zreadData.adjustmentsOnVat.total) }}</td></tr>
                <tr><td align="left">Net Amount:</td><td></td><td align="right">{{ formatClassicAmount(zreadData.netSales) }}</td></tr>
                <tr><td colspan="3">-----------------------------------------------------------------------------------------</td></tr>
                <tr><th colspan="3" style="text-align: center">TRANSACTION SUMMARY</th></tr>
                <tr><th align="left">Amount In Drawer:</th><td></td><td align="right">{{ formatClassicAmount(zreadData.amountInDrawer && zreadData.amountInDrawer.payments) }}</td></tr>
                <tr v-for="payment in paymentTypes" :key="payment.id">
                  <td style="padding-left: 5%;">{{ payment.name }}</td>
                  <td align="right">{{ getPaymentSummaryValue(`${payment.code}Count`) }}</td>
                  <td align="right">{{ formatClassicAmount(getPaymentSummaryValue(payment.code)) }}</td>
                </tr>
                <tr><th align="left">Opening Fund:</th><td></td><td align="right">{{ formatClassicAmount(zreadData.amountInDrawer && zreadData.amountInDrawer.deposit) }}</td></tr>
                <tr><th align="left">Less Withdrawal:</th><td></td><td align="right">{{ formatClassicAmount(zreadData.amountInDrawer && zreadData.amountInDrawer.withdrawal) }}</td></tr>
                <tr><th align="left">Payments Received:</th><td></td><td align="right">{{ formatClassicAmount(zreadData.amountInDrawer && zreadData.amountInDrawer.total) }}</td></tr>
              </table>
            </div>
            <div v-else class="eod-zread-preview-body">
              <br>
              Z Reading : {{ zreadData.zReadingCounter }} <br>
              Terminal : {{ zreadData.terminal }} <br>
              Printed by : {{ zreadData.printedBy }} <br>
              Date : {{ zreadData.businessDate }} <br><br>
              <table width="100%">
                <tr><th>GROSS SALES</th><td align="right">{{ integerValue(zreadData.grossSales && zreadData.grossSales.count) }}</td><td align="right">{{ formatClassicAmount(zreadData.grossSales && zreadData.grossSales.total) }}</td></tr>
                <tr><td style="padding-left: 5%;">VATABLE SALES</td><td></td><td align="right">{{ formatClassicAmount(zreadData.grossSales && zreadData.grossSales.vatable) }}</td></tr>
                <tr><td style="padding-left: 5%;">VAT AMOUNT</td><td></td><td align="right">{{ formatClassicAmount(zreadData.grossSales && zreadData.grossSales.vatAmount) }}</td></tr>
                <tr><td style="padding-left: 5%;">VAT-EXEMPT SALES</td><td></td><td align="right">{{ formatClassicAmount(zreadData.grossSales && zreadData.grossSales.vatExempt) }}</td></tr>
                <tr><td style="padding-left: 5%;">ZERO-RATED SALES</td><td></td><td align="right">{{ formatClassicAmount(zreadData.grossSales && zreadData.grossSales.zeroRated) }}</td></tr>
                <tr><td colspan="3">-----------------------------------------------------------------------------------------</td></tr>
                <tr><th>DEDUCTIONS</th><td></td><td align="right">{{ formatClassicAmount(zreadData.deductions && zreadData.deductions.total) }}</td></tr>
                <tr><td style="padding-left: 5%;">OTHER DISC.</td><td></td><td align="right">{{ formatClassicAmount(zreadData.deductions && zreadData.deductions.regular) }}</td></tr>
                <tr><td style="padding-left: 5%;">SPECIAL VATABLE DISC.</td><td></td><td align="right">{{ formatClassicAmount(zreadData.deductions && zreadData.deductions.specialVatableDiscount) }}</td></tr>
                <tr><td style="padding-left: 5%;">SPECIAL VAT EXEMPT DISC.</td><td></td><td align="right">{{ formatClassicAmount(zreadData.deductions && zreadData.deductions.specialVatExemptDiscount) }}</td></tr>
                <tr><td style="padding-left: 5%;">RETURN</td><td align="right">{{ integerValue(zreadData.deductions && zreadData.deductions.returnsCount) }}</td><td align="right">{{ formatClassicAmount(zreadData.deductions && zreadData.deductions.returns) }}</td></tr>
                <tr><td style="padding-left: 5%;">VOID</td><td align="right">{{ integerValue(zreadData.deductions && zreadData.deductions.voidCount) }}</td><td align="right">{{ formatClassicAmount(zreadData.deductions && zreadData.deductions.void) }}</td></tr>
                <tr><td style="padding-left: 5%;">MANUAL</td><td align="right">{{ integerValue(zreadData.deductions && zreadData.deductions.manualCount) }}</td><td align="right">{{ formatClassicAmount(zreadData.deductions && zreadData.deductions.manual) }}</td></tr>
                <tr><td colspan="3">-----------------------------------------------------------------------------------------</td></tr>
                <tr><th>VAT ADJUSTMENT</th><td></td><td align="right">{{ formatClassicAmount(zreadData.adjustmentsOnVat && zreadData.adjustmentsOnVat.total) }}</td></tr>
                <tr><td style="padding-left: 5%;">REGULAR</td><td></td><td align="right">{{ formatClassicAmount(zreadData.adjustmentsOnVat && zreadData.adjustmentsOnVat.regular) }}</td></tr>
                <tr><td style="padding-left: 5%;">SPECIAL VATABLE DISC.</td><td></td><td align="right">{{ formatClassicAmount(zreadData.adjustmentsOnVat && zreadData.adjustmentsOnVat.specialVatableDiscount) }}</td></tr>
                <tr><td style="padding-left: 5%;">SPECIAL VAT EXEMPT DISC.</td><td></td><td align="right">{{ formatClassicAmount(zreadData.adjustmentsOnVat && zreadData.adjustmentsOnVat.specialVatExemptDiscount) }}</td></tr>
                <tr><td style="padding-left: 5%;">VAT ON RETURN</td><td></td><td align="right">{{ formatClassicAmount(zreadData.adjustmentsOnVat && zreadData.adjustmentsOnVat.returns) }}</td></tr>
                <tr><td style="padding-left: 5%;">VAT ON VOID</td><td></td><td align="right">{{ formatClassicAmount(zreadData.adjustmentsOnVat && zreadData.adjustmentsOnVat.void) }}</td></tr>
                <tr><td style="padding-left: 5%;">VAT ON MANUAL</td><td></td><td align="right">{{ formatClassicAmount(zreadData.adjustmentsOnVat && zreadData.adjustmentsOnVat.manual) }}</td></tr>
                <tr><td colspan="3">-----------------------------------------------------------------------------------------</td></tr>
                <tr><th>NET SALES</th><td></td><td align="right">{{ formatClassicAmount(zreadData.netSales) }}</td></tr>
                <tr><td style="padding-left: 5%;">NET VAT AMOUNT</td><td></td><td align="right">{{ formatClassicAmount(zreadData.netVatAmount) }}</td></tr>
                <tr><td style="padding-left: 5%;">TOTAL QUANTITY</td><td></td><td align="right">{{ formatClassicAmount(zreadData.totalQuantity) }}</td></tr>
                <tr><td colspan="3">-----------------------------------------------------------------------------------------</td></tr>
                <tr><th>SI DETAILS</th><td></td><td></td></tr>
                <tr><td style="padding-left: 5%;">BEGINNING SI</td><td></td><td align="right">{{ integerValue(zreadData.beginningSi) }}</td></tr>
                <tr><td style="padding-left: 5%;">ENDING SI</td><td></td><td align="right">{{ integerValue(zreadData.endingSi) }}</td></tr>
                <tr><td style="padding-left: 5%;">SI COUNT</td><td></td><td align="right">{{ integerValue(zreadData.siCount) }}</td></tr>
                <tr><td colspan="3">-----------------------------------------------------------------------------------------</td></tr>
                <tr><th>TRANSACTION DETAILS</th><td></td><td></td></tr>
                <tr><td style="padding-left: 5%;">FIRST TRANSACTION</td><td></td><td align="right">{{ integerValue(zreadData.firstTransactionId) }}</td></tr>
                <tr><td style="padding-left: 5%;">LAST TRANSACTION</td><td></td><td align="right">{{ integerValue(zreadData.lastTransactionId) }}</td></tr>
                <tr><td style="padding-left: 5%;">TRANSACTION COUNT</td><td></td><td align="right">{{ integerValue(zreadData.totalTransactionCount) }}</td></tr>
              </table>
            </div>
          </div>
          <div v-else class="eod-empty-preview">Unable to load the Z-read preview.</div>
        </div>
      </template>
    </a-drawer>
  </div>
</template>

<script>
import moment from 'moment'
import HTTPRequest from '../../controllers/httpRequest.js'
import secureStorage from '../../controllers/secureStorage'

export default {
  name: 'eod-monitoring-table-new',
  props: {
    filters: {
      type: Object,
      default: () => ({})
    },
    summary: {
      type: Object,
      default: () => ({})
    },
    sites: {
      type: Array,
      default: () => []
    },
    terminals: {
      type: Array,
      default: () => []
    },
    isBranch: {
      type: Boolean,
      default: false
    },
    loadingSummary: {
      type: Boolean,
      default: false
    },
    endPoint: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      dateFormat: 'MM-DD-YYYY',
      localBusinessDate: null,
      localFilters: this.defaultFilters(),
      paymentTypes: [],
      rows: [],
      loadingRows: false,
      page: 1,
      perPage: 20,
      totalRows: 0,
      drawerVisible: false,
      selectedRow: null,
      zreadData: null,
      loadingPreview: false
    }
  },
  computed: {
    filteredRows() {
      return this.rows
    },
    healthHeadline() {
      const siteLabel = this.selectedSiteLabel
      return `${siteLabel} • ${this.localFilters.businessDate || '-'}`
    },
    healthStatusLine() {
      const pending = this.summaryValue('pending_zread_count')
      const noActivity = this.summaryValue('no_activity_count')
      const exception = this.summaryValue('exception_count')
      const total = this.summaryValue('total_terminal_count')

      if (pending > 0) {
        return `EOD is blocked by ${pending} terminal${pending === 1 ? '' : 's'} without a Z-read.`
      }

      if (noActivity > 0) {
        return `${noActivity} terminal${noActivity === 1 ? '' : 's'} had no POS activity for the selected date.`
      }

      if (exception > 0) {
        return `${exception} terminal${exception === 1 ? '' : 's'} have reconciliation discrepancies that need review.`
      }

      if (total > 0) {
        return 'All active terminals are reconciled for the selected scope.'
      }

      return 'No active terminals matched the selected scope.'
    },
    selectedSiteLabel() {
      if (!this.localFilters.siteId || this.localFilters.siteId === '0') return 'All Sites'

      const site = this.sites.find(item => String(item.id) === String(this.localFilters.siteId))
      return site ? site.name : 'Selected Site'
    },
    paginationStart() {
      if (this.totalRows === 0) return 0
      return ((this.page - 1) * this.perPage) + 1
    },
    paginationEnd() {
      if (this.totalRows === 0) return 0
      return Math.min(this.page * this.perPage, this.totalRows)
    }
  },
  async mounted() {
    this.paymentTypes = await HTTPRequest.getRequest(this.endPoint + 'payment_types/get_valid_payment_types', null)
  },
  watch: {
    filters: {
      immediate: true,
      deep: true,
      handler() {
        this.syncFromProps()
        this.page = 1
        this.fetchRows()
      }
    }
  },
  methods: {
    defaultFilters() {
      const defaultSiteId = secureStorage.getItem('defaultSiteId') || '0'
      return {
        businessDate: moment().format('YYYY-MM-DD'),
        siteId: this.isBranch ? String(defaultSiteId) : '0',
        terminalId: [],
        status: 'O',
        issueFilter: '',
        mismatchOnly: false
      }
    },
    syncFromProps() {
      const incoming = this.filters || {}
      const defaults = this.defaultFilters()
      this.localFilters = {
        businessDate: incoming.businessDate || defaults.businessDate,
        siteId: incoming.siteId ? String(incoming.siteId) : defaults.siteId,
        terminalId: Array.isArray(incoming.terminalId) ? incoming.terminalId.map(value => String(value)) : [],
        status: incoming.status || defaults.status,
        issueFilter: incoming.issueFilter || '',
        mismatchOnly: Boolean(incoming.mismatchOnly)
      }
      this.localBusinessDate = this.localFilters.businessDate ? moment(this.localFilters.businessDate) : null
    },
    summaryValue(key) {
      return Number((this.summary || {})[key] || 0)
    },
    onBusinessDateChange(value) {
      this.localBusinessDate = value
      this.localFilters.businessDate = value ? moment(value).format('YYYY-MM-DD') : ''
    },
    onSiteChange(value) {
      this.localFilters.siteId = String(value)
      this.localFilters.terminalId = []
      this.$emit('site-change', this.localFilters.siteId)
    },
    onTerminalChange(values) {
      this.localFilters.terminalId = (values || []).map(value => String(value))
    },
    applyFilters() {
      this.page = 1
      this.$emit('apply-filters', {
        businessDate: this.localFilters.businessDate,
        siteId: this.localFilters.siteId,
        terminalId: this.localFilters.terminalId,
        status: this.localFilters.status,
        issueFilter: this.localFilters.issueFilter,
        mismatchOnly: this.localFilters.mismatchOnly
      })
    },
    resetFilters() {
      this.localFilters = this.defaultFilters()
      this.localBusinessDate = this.localFilters.businessDate ? moment(this.localFilters.businessDate) : null
      this.$emit('site-change', this.localFilters.siteId)
      this.applyFilters()
    },
    buildBaseQuery() {
      const params = []
      const pushParam = (key, value) => {
        if (value === undefined || value === null || value === '' || value === '0') return
        params.push(`${key}=${encodeURIComponent(value)}`)
      }

      pushParam('business_date', this.filters.businessDate)
      pushParam('site_id', this.filters.siteId)
      if (Array.isArray(this.filters.terminalId) && this.filters.terminalId.length > 0) {
        pushParam('terminal_id', this.filters.terminalId.join(','))
      }
      pushParam('status_filter', this.filters.status)
      pushParam('issue_filter', this.filters.issueFilter)
      if (this.filters.mismatchOnly) {
        pushParam('mismatch_only', 'true')
      }

      return params.join('&')
    },
    async fetchRows() {
      if (!this.filters.businessDate) {
        this.rows = []
        this.totalRows = 0
        return
      }

      this.loadingRows = true
      try {
        const query = this.buildBaseQuery()
        const pagination = `page=${this.page}&per_page=${this.perPage}&sort_mode=severity`
        const separator = query ? '&' : ''
        const response = await HTTPRequest.getRequest(
          `${this.endPoint}z_readings/pos_document_reconciliation?${query}${separator}${pagination}`,
          null
        )
        this.rows = response && response.data ? response.data : []
        this.totalRows = Number(response && response.total ? response.total : 0)
      } catch (error) {
        this.handleRequestError(error)
      } finally {
        this.loadingRows = false
      }
    },
    refreshData() {
      this.fetchRows()
    },
    formatNumber(value) {
      return value.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')
    },
    numericValue(value) {
      const parsed = Number(value || 0)
      return Number.isFinite(parsed) ? parsed : 0
    },
    formatClassicAmount(value) {
      return this.formatNumber(this.numericValue(value).toFixed(2))
    },
    formatPadded(value, length) {
      return String(this.integerValue(value)).padStart(length, '0')
    },
    totalDiscountValue(zreadData) {
      const deductions = (zreadData && zreadData.deductions) || {}
      return this.numericValue(deductions.total) - this.numericValue(deductions.returns) - this.numericValue(deductions.void) - this.numericValue(deductions.refund)
    },
    getPaymentSummaryValue(key) {
      if (!this.zreadData || !this.zreadData.paymentSummary) return 0
      const targetKey = key.toLowerCase()
      const summary = this.zreadData.paymentSummary
      for (const currentKey in summary) {
        if (currentKey.toLowerCase() === targetKey) {
          return summary[currentKey]
        }
      }
      return 0
    },
    onPageChange(nextPage) {
      this.page = nextPage
      this.fetchRows()
    },
    rowKey(row) {
      return `${row.terminal_id}-${row.site_id}-${row.business_date}`
    },
    integerValue(value) {
      return Number(value || 0)
    },
    signedInteger(value) {
      const parsed = Number(value || 0)
      return parsed > 0 ? `+${parsed}` : `${parsed}`
    },
    formatAmount(value) {
      return Number(value || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },
    formatSignedAmount(value) {
      const parsed = Number(value || 0)
      const formatted = this.formatAmount(parsed)
      return parsed > 0 ? `+${formatted}` : formatted
    },
    statusLabel(row) {
      if (Number(row.no_activity) === 1) return 'No activity'
      if (Number(row.is_pending_zread) === 1) return 'Pending Z-read'
      return Number(row.overall_match) === 1 ? 'Completed' : 'Exception'
    },
    statusBadgeClass(row) {
      if (Number(row.no_activity) === 1) return 'badge-no-activity'
      if (Number(row.is_pending_zread) === 1) return 'badge-pending'
      return Number(row.overall_match) === 1 ? 'badge-success' : 'badge-danger'
    },
    diffTextClass(matchValue) {
      return Number(matchValue) === 1 ? 'text-success' : 'text-danger'
    },
    rowClass(row) {
      if (Number(row.no_activity) === 1) return 'is-no-activity'
      if (Number(row.is_pending_zread) === 1) return 'is-pending'
      return Number(row.overall_match) === 1 ? 'is-complete' : 'is-exception'
    },
    matchText(value) {
      return Number(value) === 1 ? 'Match' : 'Mismatch'
    },
    summaryHeadline(row) {
      if (Number(row.no_activity) === 1) return 'No POS activity was recorded for this terminal.'
      if (Number(row.is_pending_zread) === 1) return 'Z-read is still pending for this terminal.'
      if (Number(row.overall_match) === 1) return 'All reconciliation checks matched.'
      return `${row.issue_type} detected.`
    },
    summaryHint(row) {
      if (Number(row.no_activity) === 1) return 'This terminal is treated as offline / no activity for the business date because no POS activity was recorded.'
      if (Number(row.is_pending_zread) === 1) return 'Posting cannot be validated yet because the terminal has POS activity but no submitted Z-read for this business date.'
      if (Number(row.overall_match) === 1) return 'Document count, gross sales, and net sales all align with the posted POS documents.'
      return 'Review the highlighted cards below. Any card marked mismatch needs investigation before sign-off.'
    },
    summaryBannerClass(row) {
      if (Number(row.no_activity) === 1) return 'is-no-activity'
      if (Number(row.is_pending_zread) === 1) return 'is-pending'
      return Number(row.overall_match) === 1 ? 'is-match' : 'is-mismatch'
    },
    summaryCardClass(matchValue) {
      return Number(matchValue) === 1 ? 'is-match' : 'is-mismatch'
    },
    yesNo(value) {
      return Number(value) === 1 ? 'Yes' : 'No'
    },
    async openDrawer(row) {
      this.selectedRow = row
      this.drawerVisible = true
      this.zreadData = null

      if (Number(row.missing_z_reading) === 1) return

      this.loadingPreview = true
      try {
        const response = await HTTPRequest.getRequest(
          `${this.endPoint}z_readings?business_date=${row.business_date}&terminal_id=${row.terminal_id}&site_id=${row.site_id}&per_page=1`,
          null
        )
        const record = response && response.data && response.data.length > 0 ? response.data[0] : null
        this.zreadData = record ? record.reading_details : null
      } catch (error) {
        this.handleRequestError(error)
      } finally {
        this.loadingPreview = false
      }
    },
    closeDrawer() {
      this.drawerVisible = false
      this.selectedRow = null
      this.zreadData = null
    },
    async handleRequestError(error) {
      if (!error || !error.response) return

      if (error.response.status === 401) {
        const authResponse = await HTTPRequest.authenticate(this.endPoint)
        if (authResponse && authResponse.status === 'Ok') {
          this.$router.go()
        } else {
          alert('Cannot get access token. Please login again.')
          this.$router.push('/')
        }
        return
      }

      if (error.response.data && error.response.data.error === 'Session expired') {
        alert('Session is already expired. Please login again.')
        document.getElementById('logoutButton').click()
      } else if (error.response.data && error.response.data.error === 'Invalid IP for Access Token') {
        alert('Invalid Access Token. Please login again.')
        document.getElementById('logoutButton').click()
      }
    }
  }
}
</script>

<style scoped>
.eod-modern-dashboard {
  color: #1f2937;
}

.eod-toolbar-card {
  background: linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%);
}

.eod-toolbar-label {
  display: block;
  margin-bottom: 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
}

.eod-toolbar-switch {
  min-height: 32px;
  display: flex;
  align-items: center;
}

.eod-toolbar-actions {
  display: flex;
  align-items: flex-end;
}

.eod-health-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
}

.eod-overline {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #94a3b8;
}

.eod-health-metric {
  font-size: 26px;
  font-weight: 700;
  color: #111827;
}

.eod-modern-table tbody tr.is-pending {
  box-shadow: inset 4px 0 0 #f97316;
}

.eod-modern-table tbody tr.is-no-activity {
  box-shadow: inset 4px 0 0 #6b7280;
}

.eod-modern-table tbody tr.is-exception {
  box-shadow: inset 4px 0 0 #dc2626;
}

.eod-modern-table tbody tr.is-complete {
  box-shadow: inset 4px 0 0 #16a34a;
}

.eod-modern-table td,
.eod-modern-table th {
  vertical-align: middle;
}

.eod-metric-pair {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.eod-metric-pair span {
  color: #64748b;
  font-size: 12px;
}

.badge-pending {
  background-color: #f97316;
  color: #fff;
}

.badge-no-activity {
  background-color: #6b7280;
  color: #fff;
}

.eod-drawer-section + .eod-drawer-section {
  margin-top: 24px;
}

.eod-detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.eod-detail-grid span {
  display: block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #94a3b8;
}

.eod-detail-grid strong {
  display: block;
  margin-top: 4px;
  color: #111827;
}

.eod-json-preview {
  max-height: 320px;
  overflow: auto;
  background: #0f172a;
  color: #e2e8f0;
  padding: 16px;
  border-radius: 12px;
  font-size: 12px;
}


.eod-summary-banner {
  border-radius: 14px;
  padding: 16px 18px;
  border: 1px solid transparent;
}

.eod-summary-banner.is-match {
  background: #ecfdf3;
  border-color: #bbf7d0;
  color: #166534;
}

.eod-summary-banner.is-mismatch {
  background: #fef2f2;
  border-color: #fecaca;
  color: #991b1b;
}

.eod-summary-banner.is-pending {
  background: #fff7ed;
  border-color: #fed7aa;
  color: #9a3412;
}

.eod-summary-banner.is-no-activity {
  background: #f3f4f6;
  border-color: #d1d5db;
  color: #374151;
}

.eod-summary-title {
  font-size: 16px;
}

.eod-summary-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.eod-summary-card {
  border-radius: 14px;
  padding: 14px 16px;
  border: 1px solid #e5e7eb;
  background: #fff;
}

.eod-summary-card.is-match {
  background: #f0fdf4;
  border-color: #bbf7d0;
}

.eod-summary-card.is-mismatch {
  background: #fef2f2;
  border-color: #fecaca;
}

.eod-summary-card-label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
  margin-bottom: 6px;
}

.eod-summary-card strong {
  display: block;
  font-size: 16px;
  margin-bottom: 8px;
}

.eod-summary-card small {
  display: block;
  color: #475569;
}

.eod-zread-preview {
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  background: #fff;
  padding: 16px;
}

.eod-zread-preview-body {
  font-family: 'Palatino Linotype', Palatino, serif;
  font-size: 12px;
  color: #111827;
}

.eod-zread-preview-body table td,
.eod-zread-preview-body table th {
  padding: 2px 0;
  vertical-align: top;
}

.eod-empty-preview {
  border: 1px dashed #cbd5e1;
  border-radius: 14px;
  background: #f8fafc;
  color: #64748b;
  padding: 20px;
  text-align: center;
}

@media (max-width: 991px) {
  .eod-toolbar-actions {
    justify-content: flex-start;
  }

  .eod-detail-grid {
    grid-template-columns: 1fr;
  }

  .eod-summary-grid {
    grid-template-columns: 1fr;
  }
}
</style>
